import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useStore } from '../../store';

/**
 * Footer Component
 * 
 * Responsive footer for the Spellion application.
 * Contains copyright, legal links, and social links.
 * 
 * @validates Requirements 10.1, 10.2, 11.3
 */
export function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();
  const isAuthenticated = useStore((state) => state.isAuthenticated);

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="container-app py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand and Description */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-white">
              <img 
                src="/spellion.svg" 
                alt="Spellion" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-display font-bold text-xl">Spellion</span>
            </div>
            <p className="text-sm text-slate-400 max-w-xs">
              {t('footer.brand.description')}
            </p>
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white">{t('footer.navigation.title')}</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.navigation.search')}
              </Link>
              {isAuthenticated ? (
                <Link to="/account" className="text-sm text-slate-400 hover:text-white transition-colors">
                  {t('footer.navigation.myAccount')}
                </Link>
              ) : (
                <>
                  <Link to="/register" className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('footer.navigation.createAccount')}
                  </Link>
                  <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('footer.navigation.login')}
                  </Link>
                </>
              )}
            </nav>
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white">{t('footer.legal.title')}</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.legal.about')}
              </Link>
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.legal.privacy')}
              </Link>
              <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.legal.terms')}
              </Link>
              <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.legal.contact')}
              </Link>
            </nav>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
          <p>{t('footer.copyright').replace('{year}', String(currentYear))}</p>
          <p className="mt-1">
            {t('footer.dictionaryNote')}
          </p>
        </div>
      </div>
    </footer>
  );
}
